import React, { useEffect, useRef } from 'react';
import { FullscreenProps } from './types';
import { addClass, removeClass } from '@frontendUtils/helpers';
import './Scss/full-screen.scss';

const Fullscreen = (props: FullscreenProps) => {
    const TransitionTime = 200;
    const containerRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    // Utility: find focusable elements within container
    const getFocusableElements = () => {
        if (!containerRef.current) return [];
        const elements =  Array.from(
            containerRef.current.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), textarea, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
        );

        const filtered = elements.filter((el) => {
            if ((el as any).disabled) return false;

            const style = window.getComputedStyle(el);
            if (style.display === 'none' || style.visibility === 'hidden') return false;

            if (!el.offsetParent && style.position !== 'fixed') return false;
            return typeof el.focus === 'function';
        });

        return filtered;
    };

    // Focus trapping handler
    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') {
            return;
        }

        const focusableElementsArray = getFocusableElements();

        if (!focusableElementsArray.length) {
            return;
        }

        const firstElement = focusableElementsArray[0];
        const lastElement = focusableElementsArray[focusableElementsArray.length - 1];
        const activeElement = document.activeElement as HTMLElement;
        const isShift = e.shiftKey;

        if (isShift) {
            // SHIFT + TAB
            if (activeElement === firstElement || activeElement === containerRef.current) {
                (lastElement as HTMLElement).focus();
                e.preventDefault();
            }
        } else {
            // TAB forward
            if (activeElement === lastElement || activeElement === containerRef.current) {
                (firstElement as HTMLElement).focus();
                e.preventDefault();
            }
        }
    };

    // Open/close logic
    useEffect(() => {
        if (props.open) {
            previousActiveElement.current = document.activeElement as HTMLElement;
            setTimeout(() => {
                document.body.style.position = 'fixed';
                addClass(document.body, 'fullscreen-open');

                const focusables = getFocusableElements();
                if (focusables.length > 0) {
                    focusables[0].focus();
                }

                document.addEventListener('keydown', handleKeyDown);

                if (props.onShown) props.onShown();
            }, TransitionTime);
        } else {
            document.body.style.position = 'relative';
            removeClass(document.body, 'fullscreen-open');
            document.removeEventListener('keydown', handleKeyDown);
            previousActiveElement.current?.focus();
        }
    }, [props.open]);

    const handleContentReady = () => {
        const focusables = getFocusableElements();
        if (focusables.length > 0) {
            focusables[0].focus();
        }
    };

    // Refocus when async fullscreen content has finished rendering
    useEffect(() => {
        if (!props.open) return;

        window.addEventListener("fullscreen:content-ready", handleContentReady);

        return () => {
            window.removeEventListener("fullscreen:content-ready", handleContentReady);
        };
    }, [props.open]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            document.body.style.position = 'relative';
            removeClass(document.body, 'fullscreen-open');
            document.removeEventListener('keydown', handleKeyDown);
            previousActiveElement.current?.focus();
        };
    }, []);

    return (
        <div ref={containerRef} className={`full-screen ${props.open ? 'open' : ''}`}>
            <div className="full-screen__message" id="fullscreenServerMessageContainer"></div>
            <div className="full-screen__message" id="fullscreenInformationMessageContainer"></div>
            {props.children}
        </div>
    );
};

export default Fullscreen;
